/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from "react";
import {
  Trophy, Plus, Trash2, Users, ChevronRight, CheckCircle, XCircle,
  Clock, Zap, Shield, Calendar, Eye, Download,
  AlertTriangle, BarChart3,
} from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";

// ── Types ─────────────────────────────────────────────────────────────────────

interface LfsGame {
  id: number;
  title: string;
  description: string | null;
  isOpen: boolean;
  isActive: boolean;
  isFinished: boolean;
  createdAt: string;
  _count: { participants: number; rounds: number };
}

interface LfsParticipant {
  id: number;
  userId: number;
  isEliminated: boolean;
  eliminatedInRound: number | null;
  joinedAt: string;
  user: { id: number; name: string; username: string | null; email: string; avatar: string | null };
  picks: { teamPicked: string; isCorrect: boolean | null; round: { roundNumber: number; label: string } }[];
}

interface LfsRound {
  id: number;
  gameId: number;
  roundNumber: number;
  label: string;
  deadline: string;
  isProcessed: boolean;
  winningTeams: string;
  gameweekId?: number | null;
  gameweek?: { id: number; name: string } | null;
  _count: { picks: number };
}

type DetailTab = "rounds" | "participants";
type ParticipantFilter = "all" | "active" | "eliminated";

// ── Page ──────────────────────────────────────────────────────────────────────

export default function LastFanStandingPage() {
  const qc = useQueryClient();
  const [selectedGame, setSelectedGame] = useState<LfsGame | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>("rounds");
  const [participantFilter, setParticipantFilter] = useState<ParticipantFilter>("all");

  /** Click handler for the stat cards — jumps to the Participants tab with
   *  the appropriate filter pre-selected. */
  const jumpToParticipants = (filter: ParticipantFilter) => {
    setParticipantFilter(filter);
    setDetailTab("participants");
  };

  // Dialogs
  const [createGameOpen, setCreateGameOpen] = useState(false);
  const [processOpen, setProcessOpen] = useState(false);
  const [processRound, setProcessRound] = useState<LfsRound | null>(null);
  const [viewPicksRound, setViewPicksRound] = useState<LfsRound | null>(null);

  // Forms
  const [gameForm, setGameForm] = useState({ title: "", description: "" });
  const [selectedWinners, setSelectedWinners] = useState<string[]>([]);

  const invalidateGames = () => qc.invalidateQueries({ queryKey: ["lfs", "games"] });
  const invalidateGame = (id: number) => {
    qc.invalidateQueries({ queryKey: ["lfs", "rounds", id] });
    qc.invalidateQueries({ queryKey: ["lfs", "participants", id] });
    invalidateGames();
  };

  // ── Queries ───────────────────────────────────────────────────────────────

  const { data: rawGames, isLoading: gamesLoading } = useQuery<LfsGame[]>({
    queryKey: ["lfs", "games"],
    queryFn: () => apiClient.get(API_ENDPOINTS.LFS.GAMES),
    select: (r: any) => (Array.isArray(r) ? r : (Array.isArray(r?.games) ? r.games : [])),
  });
  const games: LfsGame[] = rawGames ?? [];

  const { data: rounds = [] } = useQuery<LfsRound[]>({
    queryKey: ["lfs", "rounds", selectedGame?.id],
    queryFn: () => apiClient.get(API_ENDPOINTS.LFS.ROUNDS(selectedGame!.id)),
    select: (r: any) => (Array.isArray(r) ? r : []),
    enabled: !!selectedGame,
  });

  const { data: participants = [] } = useQuery<LfsParticipant[]>({
    queryKey: ["lfs", "participants", selectedGame?.id],
    queryFn: () => apiClient.get(API_ENDPOINTS.LFS.PARTICIPANTS(selectedGame!.id)),
    select: (r: any) => (Array.isArray(r) ? r : []),
    enabled: !!selectedGame,
  });

  const { data: roundPicks = [] } = useQuery<any[]>({
    queryKey: ["lfs", "picks", viewPicksRound?.id],
    queryFn: () => apiClient.get(API_ENDPOINTS.LFS.ROUND_PICKS(viewPicksRound!.id)),
    select: (r: any) => (Array.isArray(r) ? r : []),
    enabled: !!viewPicksRound,
  });

  const { data: processRoundPicks = [] } = useQuery<any[]>({
    queryKey: ["lfs", "picks", processRound?.id],
    queryFn: () => apiClient.get(API_ENDPOINTS.LFS.ROUND_PICKS(processRound!.id)),
    select: (r: any) => (Array.isArray(r) ? r : []),
    enabled: !!processRound && processOpen,
  });

  type SuggestedWinnersResult = {
    winners: string[];
    draws: { homeNation: string; awayNation: string }[];
    matchCount: number;
  };
  const { data: suggestedWinners, isFetching: loadingSuggested, refetch: fetchSuggested } = useQuery<SuggestedWinnersResult>({
    queryKey: ["lfs", "suggested-winners", processRound?.id],
    // apiClient's response interceptor unwraps .data — use axios's dual-generic
    // `get<T, T>` so the return type matches runtime (T, not AxiosResponse<T>).
    queryFn: () =>
      apiClient.get<SuggestedWinnersResult, SuggestedWinnersResult>(
        API_ENDPOINTS.LFS.SUGGESTED_WINNERS(processRound!.id),
      ),
    enabled: false,
  });

  const picksByTeam = useMemo(() => {
    const map = new Map<string, number>();
    for (const pk of processRoundPicks) {
      map.set(pk.teamPicked as string, (map.get(pk.teamPicked) ?? 0) + 1);
    }
    return Array.from(map.entries())
      .map(([team, count]) => ({ team, count }))
      .sort((a, b) => b.count - a.count);
  }, [processRoundPicks]);

  const toggleWinner = (team: string) =>
    setSelectedWinners((p) => p.includes(team) ? p.filter((t) => t !== team) : [...p, team]);

  const handleLoadSuggested = async () => {
    const result = await fetchSuggested();
    if (result.data?.winners?.length) {
      setSelectedWinners(result.data.winners);
      toast.success(`Loaded ${result.data.winners.length} winning nations from match results`);
    } else {
      toast.info("No conclusive winners found — draws or no finished matches");
    }
  };

  // ── Mutations ─────────────────────────────────────────────────────────────

  const { mutate: createGame, isPending: creatingGame } = useMutation({
    mutationFn: (d: any) => apiClient.post(API_ENDPOINTS.LFS.GAMES, d),
    onSuccess: () => { toast.success("Game created — registration is now open"); invalidateGames(); setCreateGameOpen(false); setGameForm({ title: "", description: "" }); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed"),
  });

  const { mutate: deleteGame } = useMutation({
    mutationFn: (id: number) => apiClient.delete(API_ENDPOINTS.LFS.GAME(id)),
    onSuccess: () => { toast.success("Deleted"); invalidateGames(); setSelectedGame(null); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed"),
  });

  const { mutate: processRoundMutation, isPending: processing } = useMutation({
    mutationFn: ({ roundId, teams }: { roundId: number; teams: string[] }) =>
      apiClient.post<{ eliminated: number }, { eliminated: number }>(
        API_ENDPOINTS.LFS.PROCESS_ROUND(roundId),
        { winningTeams: teams },
      ),
    onSuccess: (r) => {
      toast.success(`Round processed — ${r?.eliminated ?? "?"} eliminated`);
      invalidateGame(selectedGame!.id);
      setProcessOpen(false);
      setProcessRound(null);
      setSelectedWinners([]);
    },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed"),
  });

  const active = participants.filter((p) => !p.isEliminated);
  const eliminated = participants.filter((p) => p.isEliminated);
  const processedRounds = rounds.filter((r) => r.isProcessed).length;

  // ── Game List ─────────────────────────────────────────────────────────────

  if (!selectedGame) {
    return (
      <Layout>
        {/* Page header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Trophy className="h-5 w-5 text-yellow-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Last Fan Standing</h1>
            </div>
            <p className="text-sm text-gray-500 ml-13">Survivor-style pick'em — one wrong pick and you're out</p>
          </div>
          <Button onClick={() => setCreateGameOpen(true)} className="bg-yellow-500 hover:bg-yellow-600 text-white gap-2">
            <Plus className="h-4 w-4" /> New Game
          </Button>
        </div>

        {gamesLoading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
          </div>
        ) : games.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="h-7 w-7 text-gray-300" />
            </div>
            <p className="font-semibold text-gray-700 mb-1">No games yet</p>
            <p className="text-sm text-gray-400 mb-6">Create your first Last Fan Standing game to get started.</p>
            <Button onClick={() => setCreateGameOpen(true)} className="bg-yellow-500 hover:bg-yellow-600 text-white gap-2">
              <Plus className="h-4 w-4" /> Create Game
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {games.map((g) => {
              const hasRounds = g._count.rounds > 0;
              const statusColor = g.isFinished
                ? "bg-gray-100 text-gray-500"
                : hasRounds
                  ? "bg-green-100 text-green-700"
                  : "bg-blue-100 text-blue-700";
              const statusLabel = g.isFinished
                ? "Finished"
                : hasRounds
                  ? "Active"
                  : "Waiting for first gameweek";
              return (
                <div key={g.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={cn("text-xs font-semibold px-2.5 py-1 rounded-full", statusColor)}>
                          {statusLabel}
                        </span>
                      </div>
                      <h2 className="text-lg font-bold text-gray-900 mb-1">{g.title}</h2>
                      {g.description && (
                        <p className="text-sm text-gray-500 mb-3">{g.description}</p>
                      )}
                      <div className="flex gap-5 text-sm text-gray-500">
                        <span className="flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5" />
                          {g._count.participants} participants
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5" />
                          {g._count.rounds} rounds
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setSelectedGame(g); setDetailTab("rounds"); }}
                        className="gap-1.5"
                      >
                        Manage <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { if (confirm(`Delete "${g.title}"?`)) deleteGame(g.id); }}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create Game Dialog */}
        <Dialog open={createGameOpen} onOpenChange={setCreateGameOpen}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" /> New Game
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="mb-1.5 block text-sm font-medium">Game Title</Label>
                <Input
                  value={gameForm.title}
                  onChange={(e) => setGameForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="FIFA World Cup 2026 — Last Fan Standing"
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm font-medium">Description <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Textarea
                  value={gameForm.description}
                  onChange={(e) => setGameForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="resize-none"
                  placeholder="Pick a team each gameweek — don't get eliminated!"
                />
              </div>
              <div className="bg-blue-50 rounded-lg px-3 py-2.5 text-xs text-blue-700 flex items-start gap-2">
                <Zap className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Registration opens automatically when the game is created.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateGameOpen(false)}>Cancel</Button>
              <Button
                disabled={creatingGame}
                onClick={() => createGame(gameForm)}
                className="bg-yellow-500 hover:bg-yellow-600 text-white"
              >
                {creatingGame ? "Creating…" : "Create Game"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Layout>
    );
  }

  // ── Game Detail ───────────────────────────────────────────────────────────

  const game = selectedGame;
  const hasRounds = rounds.length > 0;
  const statusLabel = game.isFinished
    ? "Finished"
    : hasRounds
      ? "Active"
      : "Waiting for first gameweek";
  const statusDot = game.isFinished
    ? "bg-gray-400"
    : hasRounds
      ? "bg-green-500"
      : "bg-blue-500";

  return (
    <Layout>
      {/* Breadcrumb + Header */}
      <div className="mb-6">
        <button
          onClick={() => setSelectedGame(null)}
          className="text-sm text-gray-400 hover:text-gray-600 flex items-center gap-1 mb-4"
        >
          ← All Games
        </button>

        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={cn("w-2 h-2 rounded-full", statusDot)} />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {statusLabel}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{game.title}</h1>
            {game.description && (
              <p className="text-sm text-gray-500 mt-0.5">{game.description}</p>
            )}
          </div>

          {/* Game actions */}
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={async () => {
                try {
                  const csv = await apiClient.get(API_ENDPOINTS.LFS.EXPORT_CSV(game.id), {
                    responseType: "text",
                  });
                  const blob = new Blob([csv as unknown as string], { type: "text/csv;charset=utf-8" });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `lfs-${game.title.replace(/[^a-z0-9]/gi, "-").toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success("Export downloaded");
                } catch (e: any) {
                  toast.error(e?.response?.data?.message || "Failed to export");
                }
              }}
            >
              <Download className="h-4 w-4" /> Export CSV
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-400 hover:text-red-600 hover:bg-red-50"
              onClick={() => { if (confirm(`Delete "${game.title}"?`)) deleteGame(game.id); }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Stats row — first three are clickable shortcuts to the filtered Participants tab */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[
          {
            icon: Users,
            label: "Total Players",
            value: participants.length,
            color: "text-gray-900",
            bg: "bg-gray-50",
            filter: "all" as ParticipantFilter,
          },
          {
            icon: Shield,
            label: "Still Standing",
            value: active.length,
            color: "text-green-700",
            bg: "bg-green-50",
            filter: "active" as ParticipantFilter,
          },
          {
            icon: XCircle,
            label: "Eliminated",
            value: eliminated.length,
            color: "text-red-600",
            bg: "bg-red-50",
            filter: "eliminated" as ParticipantFilter,
          },
          {
            icon: BarChart3,
            label: "Rounds Done",
            value: `${processedRounds}/${rounds.length}`,
            color: "text-yellow-700",
            bg: "bg-yellow-50",
            filter: null,
          },
        ].map((s) => {
          const isClickable = s.filter !== null;
          const isActive =
            isClickable &&
            detailTab === "participants" &&
            participantFilter === s.filter;
          const content = (
            <>
              <s.icon className={cn("h-5 w-5 shrink-0", s.color)} />
              <div className="text-left">
                <p className={cn("text-xl font-bold", s.color)}>{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </>
          );
          if (!isClickable) {
            return (
              <div key={s.label} className={cn("rounded-xl border p-4 flex items-center gap-3", s.bg)}>
                {content}
              </div>
            );
          }
          return (
            <button
              key={s.label}
              type="button"
              onClick={() => jumpToParticipants(s.filter as ParticipantFilter)}
              title={`View ${s.label.toLowerCase()}`}
              className={cn(
                "rounded-xl border p-4 flex items-center gap-3 transition cursor-pointer text-left",
                s.bg,
                isActive
                  ? "ring-2 ring-offset-1 ring-gray-400/50 border-gray-400"
                  : "hover:shadow-sm hover:border-gray-300",
              )}
            >
              {content}
            </button>
          );
        })}
      </div>

      {/* Auto-process info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-5 flex items-start gap-2">
        <Zap className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Rounds are automatic — a new round appears when you <strong>create a gameweek</strong> in Gameweek Management,
          and it auto-processes when that gameweek is <strong>completed</strong> (winners derived from match results).
          Use <em>Override</em> only if auto-processing misses a round.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-5 w-fit">
        {(["rounds", "participants"] as DetailTab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setDetailTab(tab)}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-lg transition-all",
              detailTab === tab
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            {tab === "rounds"
              ? `Rounds (${rounds.length})`
              : `Participants (${participants.length})`}
          </button>
        ))}
      </div>

      {/* ── Rounds Tab ─────────────────────────────────────────────────────── */}
      {detailTab === "rounds" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">
              {rounds.length === 0
                ? "No rounds yet — create a gameweek in Gameweek Management to start."
                : `${processedRounds} of ${rounds.length} rounds processed`}
            </p>
          </div>

          {rounds.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-16 text-center">
              <Calendar className="h-8 w-8 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">Rounds appear here when gameweeks are created</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border overflow-hidden">
              {/* Table header */}
              <div className="grid grid-cols-12 px-4 py-3 border-b bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <div className="col-span-1">#</div>
                <div className="col-span-3">Label</div>
                <div className="col-span-2">Gameweek</div>
                <div className="col-span-3">Deadline</div>
                <div className="col-span-1 text-center">Picks</div>
                <div className="col-span-1 text-center">Status</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>

              {rounds.map((r, i) => (
                <div
                  key={r.id}
                  className={cn(
                    "grid grid-cols-12 px-4 py-3.5 items-center gap-2 text-sm",
                    i !== rounds.length - 1 && "border-b",
                    r.isProcessed ? "bg-white" : "bg-white hover:bg-gray-50"
                  )}
                >
                  {/* # */}
                  <div className="col-span-1">
                    <span className="w-6 h-6 rounded-full bg-yellow-100 text-yellow-700 text-xs font-bold flex items-center justify-center">
                      {r.roundNumber}
                    </span>
                  </div>

                  {/* Label */}
                  <div className="col-span-3">
                    <p className="font-medium text-gray-900 truncate">{r.label}</p>
                  </div>

                  {/* Gameweek */}
                  <div className="col-span-2">
                    {r.gameweek ? (
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                        {r.gameweek.name}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Not linked</span>
                    )}
                  </div>

                  {/* Deadline */}
                  <div className="col-span-3 text-xs text-gray-500">
                    {new Date(r.deadline).toLocaleString(undefined, {
                      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                    })}
                  </div>

                  {/* Picks */}
                  <div className="col-span-1 text-center text-xs font-semibold text-gray-700">
                    {r._count.picks}
                  </div>

                  {/* Status */}
                  <div className="col-span-1 flex justify-center">
                    {r.isProcessed ? (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Done</span>
                    ) : new Date() > new Date(r.deadline) ? (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">Overdue</span>
                    ) : (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Open</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex justify-end gap-1">
                    <button
                      onClick={() => setViewPicksRound(r)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      title="View picks"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                    {!r.isProcessed && (
                      <button
                        onClick={() => { setProcessRound(r); setSelectedWinners([]); setProcessOpen(true); }}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 transition-colors"
                        title="Override process (emergency)"
                      >
                        <AlertTriangle className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Participants Tab ───────────────────────────────────────────────── */}
      {detailTab === "participants" && (
        <div className="space-y-5">
          {/* Filter pills — driven either directly here or pre-set by clicking a stat card */}
          {participants.length > 0 && (
            <div className="flex gap-2">
              {([
                { value: "all" as ParticipantFilter, label: `All · ${participants.length}` },
                { value: "active" as ParticipantFilter, label: `Still Standing · ${active.length}` },
                { value: "eliminated" as ParticipantFilter, label: `Eliminated · ${eliminated.length}` },
              ]).map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setParticipantFilter(p.value)}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-xs font-semibold transition border",
                    participantFilter === p.value
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-300",
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}

          {participants.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-16 text-center">
              <Users className="h-8 w-8 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">No participants yet</p>
            </div>
          ) : (
            <>
              {/* Empty-state per filter when the filter excludes everyone */}
              {participantFilter === "active" && active.length === 0 && (
                <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-12 text-center">
                  <Shield className="h-7 w-7 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">No survivors left — everyone's been eliminated.</p>
                </div>
              )}
              {participantFilter === "eliminated" && eliminated.length === 0 && (
                <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-12 text-center">
                  <XCircle className="h-7 w-7 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">No eliminations yet — every player is still in.</p>
                </div>
              )}

              {/* Still Standing */}
              {active.length > 0 && participantFilter !== "eliminated" && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="h-4 w-4 text-green-600" />
                    <p className="text-sm font-semibold text-green-700">Still Standing — {active.length}</p>
                  </div>
                  <div className="bg-white rounded-2xl border overflow-hidden">
                    {active.map((p, i) => (
                      <div key={p.id} className={cn("flex items-center gap-3 px-4 py-3", i !== active.length - 1 && "border-b")}>
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0 text-xs font-bold text-green-700">
                          {p.user.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{p.user.name}</p>
                          <p className="text-xs text-gray-400">{p.user.username ? `@${p.user.username}` : p.user.email}</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {p.picks.map((pk) => (
                            <span
                              key={pk.round.roundNumber}
                              title={`R${pk.round.roundNumber}: ${pk.teamPicked}`}
                              className={cn(
                                "text-xs px-2 py-0.5 rounded-full font-semibold",
                                pk.isCorrect === true ? "bg-green-100 text-green-700" :
                                pk.isCorrect === false ? "bg-red-100 text-red-600" :
                                "bg-gray-100 text-gray-500"
                              )}
                            >
                              {pk.teamPicked.slice(0, 3).toUpperCase()}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Eliminated */}
              {eliminated.length > 0 && participantFilter !== "active" && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <p className="text-sm font-semibold text-red-600">Eliminated — {eliminated.length}</p>
                  </div>
                  <div className="bg-white rounded-2xl border overflow-hidden">
                    {eliminated.map((p, i) => (
                      <div key={p.id} className={cn("flex items-center gap-3 px-4 py-3 opacity-60", i !== eliminated.length - 1 && "border-b")}>
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0 text-xs font-bold text-red-600">
                          {p.user.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{p.user.name}</p>
                          <p className="text-xs text-red-400">Out in Round {p.eliminatedInRound}</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {p.picks.map((pk) => (
                            <span
                              key={pk.round.roundNumber}
                              title={`R${pk.round.roundNumber}: ${pk.teamPicked}`}
                              className={cn(
                                "text-xs px-2 py-0.5 rounded-full font-semibold",
                                pk.isCorrect === true ? "bg-green-100 text-green-700" :
                                pk.isCorrect === false ? "bg-red-100 text-red-600" :
                                "bg-gray-100 text-gray-500"
                              )}
                            >
                              {pk.teamPicked.slice(0, 3).toUpperCase()}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Override Process Dialog ────────────────────────────────────────── */}
      <Dialog open={processOpen} onOpenChange={(v) => { if (!v) { setProcessOpen(false); setSelectedWinners([]); } }}>
        <DialogContent className="max-w-md bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Override: Round {processRound?.roundNumber} — {processRound?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800">
              Manual override — only use this if auto-processing didn't trigger. Check the nations that <strong>won</strong> their match.
            </div>

            {processRound?.gameweekId && (
              <Button
                size="sm"
                variant="outline"
                className="w-full gap-2 border-blue-200 text-blue-700 hover:bg-blue-50"
                disabled={loadingSuggested}
                onClick={handleLoadSuggested}
              >
                <Zap className="h-4 w-4" />
                {loadingSuggested ? "Loading…" : "Load from Match Results"}
              </Button>
            )}

            {suggestedWinners && (
              <div className="bg-blue-50 rounded-lg px-3 py-2 text-xs text-blue-700">
                Based on {suggestedWinners.matchCount} finished match{suggestedWinners.matchCount !== 1 ? "es" : ""}.
                {suggestedWinners.draws.length > 0 && (
                  <span className="block mt-0.5 text-amber-600">
                    Draws (no winner): {suggestedWinners.draws.map((d) => `${d.homeNation} vs ${d.awayNation}`).join(", ")}
                  </span>
                )}
              </div>
            )}

            {picksByTeam.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No picks submitted yet</p>
            ) : (
              <div className="space-y-1.5">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                  Select winning nations ({processRoundPicks.length} picks total)
                </p>
                {picksByTeam.map(({ team, count }) => {
                  const checked = selectedWinners.includes(team);
                  return (
                    <label
                      key={team}
                      className={cn(
                        "flex items-center gap-3 rounded-xl px-3 py-2.5 cursor-pointer border transition-all",
                        checked ? "bg-green-50 border-green-300" : "bg-gray-50 border-transparent hover:border-gray-200"
                      )}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleWinner(team)} className="w-4 h-4 accent-green-500" />
                      <span className="flex-1 font-medium text-sm text-gray-800">{team}</span>
                      <span className={cn(
                        "text-xs font-semibold px-2 py-0.5 rounded-full",
                        checked ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"
                      )}>
                        {count} pick{count !== 1 ? "s" : ""}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            {selectedWinners.length > 0 && (
              <p className="text-xs text-green-700 font-medium flex items-center gap-1">
                <CheckCircle className="h-3.5 w-3.5" />
                {selectedWinners.length} winning nation{selectedWinners.length !== 1 ? "s" : ""} selected
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setProcessOpen(false); setSelectedWinners([]); }}>Cancel</Button>
            <Button
              disabled={processing || !selectedWinners.length}
              onClick={() => {
                if (!selectedWinners.length) { toast.error("Select at least one winning nation"); return; }
                processRoundMutation({ roundId: processRound!.id, teams: selectedWinners });
              }}
              className="bg-yellow-500 hover:bg-yellow-600 text-white"
            >
              {processing ? "Processing…" : "Process Round"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── View Picks Dialog ──────────────────────────────────────────────── */}
      <Dialog open={!!viewPicksRound} onOpenChange={(v) => { if (!v) setViewPicksRound(null); }}>
        <DialogContent className="max-w-lg bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Round {viewPicksRound?.roundNumber} Picks
              <span className="text-gray-400 font-normal"> — {viewPicksRound?.label}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            {roundPicks.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No picks submitted yet</p>
            ) : (
              roundPicks.map((pk: any) => (
                <div key={pk.id} className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2.5">
                  <div className="w-7 h-7 rounded-full bg-white border flex items-center justify-center text-xs font-bold text-gray-600 shrink-0">
                    {pk.participant.user.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{pk.participant.user.name}</p>
                    <p className="text-xs text-gray-400">{pk.participant.user.username ? `@${pk.participant.user.username}` : pk.participant.user.email}</p>
                  </div>
                  <span className="font-semibold text-sm text-gray-700 mr-1">{pk.teamPicked}</span>
                  {pk.isCorrect === true && <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />}
                  {pk.isCorrect === false && <XCircle className="h-4 w-4 text-red-500 shrink-0" />}
                  {pk.isCorrect === null && <Clock className="h-4 w-4 text-gray-400 shrink-0" />}
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewPicksRound(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
